/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-04-22 21:02:08
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { Autocomplete, TextContainer, InlineError } from '@shopify/polaris';

import CustomList from './custom-list';
import { RenderLabelTooltip } from '../forms/render-label-tooltip';

type Props = {
  input: Object,
  options: Array<{ value: string, label: string }>,
  label: string,
  textHelper: string,
  meta: Object,
  tooltip: string,
  suggestionTitle: string,
  placeholder: string,
  helpText: string
};

type State = {
  loading: boolean,
  deselectedOptions: Array<Object>,
  options: Array<Object>,
  selectedOptions: Array<Object>,
  displaySelectedOptions: Array<Object>,
  inputValue: string,
  visibleOptionIndex: number,
  latestSearchIndex: number
};

export class RenderAutocomplete extends Component<Props, State> {
  paginationInterval = 25;
  timeoutTextChange = undefined;

  state = {
    loading: false,
    deselectedOptions: [],
    options: [],
    selectedOptions: [],
    displaySelectedOptions: [],
    inputValue: '',
    visibleOptionIndex: 0,
    latestSearchIndex: 0
  };

  componentDidMount() {
    const { options: allOptions, input = {} } = this.props;
    const value = _.get(input, 'value', []);

    // Get option value and save as selected options
    const selectedOptions = _.isArray(value) ? value.map(option => option.value) : [];

    // Get options for first page
    const firstPageOptions = _.slice(allOptions, 0, this.paginationInterval);

    this.setState({
      deselectedOptions: firstPageOptions,
      options: firstPageOptions,
      selectedOptions,
      // <!> Difficult bug! We need to clone input value to avoid impact initial values for redux field. That helps to recognize the change on field
      displaySelectedOptions: _.isArray(value) ? _.cloneDeep(value) : [],
      visibleOptionIndex: this.paginationInterval // First page
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    const { input = {} } = nextProps;
    const value = _.get(input, 'value', []);

    const { options: allOptions } = this.props;
    const { options: allOptionsNext } = nextProps;
    const { deselectedOptions, options } = this.state;

    let state = {};

    // Get option value and save as selected options
    const selectedOptions = _.isArray(value) ? value.map(option => option.value) : [];
    state.selectedOptions = selectedOptions;
    state.displaySelectedOptions = _.isArray(value) ? _.cloneDeep(value) : [];

    // Get options for first page
    const firstPageOptions = _.slice(allOptionsNext, 0, this.paginationInterval);

    // Init options
    if (!options.length || allOptions.length !== allOptionsNext.length) {
      state.options = firstPageOptions;
    }

    // Init deselectedOptions
    if (!deselectedOptions.length || allOptions.length !== allOptionsNext.length) {
      state.deselectedOptions = firstPageOptions;
    }

    this.setState(state);
  }

  componentWillUnmount() {
    // Reset states
    this.setState({
      loading: false,
      deselectedOptions: [],
      options: [],
      selectedOptions: [],
      displaySelectedOptions: [],
      inputValue: '',
      visibleOptionIndex: 0
    });
  }

  render() {
    const { options, selectedOptions, displaySelectedOptions, loading } = this.state;
    const { label, suggestionTitle, placeholder, textHelper, meta, tooltip } = this.props;

    const textField = (
      <Autocomplete.TextField
        onChange={this.onTextChange}
        label={label ? <RenderLabelTooltip label={label} tooltip={tooltip} /> : undefined}
        value={this.state.inputValue}
        placeholder={placeholder}
      />
    );

    return (
      <div>
        <Autocomplete
          allowMultiple
          options={options}
          selected={selectedOptions}
          textField={textField}
          onSelect={this.onSelect}
          listTitle={suggestionTitle}
          onLoadMoreResults={this.onLoadMoreResults}
          preferredPosition="above"
          willLoadMoreResults={true}
          loading={loading}
        />

        {selectedOptions.length > 0 ? (
          <div className="BC-Autocomplete__TextContainer">
            <TextContainer>
              <div>
                <CustomList list={displaySelectedOptions} onRemove={this.onRemoveTag} />
              </div>
            </TextContainer>
          </div>
        ) : (
          ''
        )}

        <div className="Polaris-Labelled__HelpText">{textHelper}</div>
        {meta.error ? <InlineError message={meta.error} /> : ''}
      </div>
    );
  }

  onRemoveTag = (tag: { value: string, label: string }) => {
    const { selectedOptions, displaySelectedOptions } = this.state;

    // Remove from display selected options
    _.remove(displaySelectedOptions, item => {
      return item.value === tag.value;
    });

    _.remove(selectedOptions, item => {
      return item === tag.value;
    });

    this.setState({ selectedOptions, displaySelectedOptions });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  onTextChange = (value: string) => {
    const { options: allOptions } = this.props;

    // Show loading icon before updating
    this.setState({ loading: true, options: [], inputValue: value });

    clearTimeout(this.timeoutTextChange);

    // Using timeout for reinitialize component, avoid to display catched values
    this.timeoutTextChange = setTimeout(() => {
      // Re-init option list
      if (value === '') {
        // Get options for first page
        const firstPageOptions = _.slice(allOptions, 0, this.paginationInterval);

        this.setState({
          deselectedOptions: firstPageOptions,
          options: firstPageOptions,
          visibleOptionIndex: this.paginationInterval,
          loading: false
        });
        return;
      }

      // Filter option list by input text
      const { filterOptions = [], latestSearchIndex } = this.filter(
        value,
        allOptions,
        0,
        this.paginationInterval
      );

      this.setState({
        deselectedOptions: filterOptions,
        options: filterOptions,
        visibleOptionIndex: this.paginationInterval,
        latestSearchIndex,
        loading: false
      });
    }, 100);
  };

  onSelect = (items: Object) => {
    let { selectedOptions } = this.state;

    // Add new item
    if (selectedOptions.length < items.length) {
      this.addNewItem(items);
    } else {
      // Remove item
      this.removeItem(items);
    }
  };

  addNewItem = (items: Object) => {
    let { deselectedOptions, selectedOptions = [], displaySelectedOptions = [] } = this.state;

    // Find new item
    const newItems = _.difference(items, selectedOptions);

    // Push new item to saved list
    newItems.forEach(selectedItem => {
      const matchedOption = _.find(deselectedOptions, { value: selectedItem });

      if (matchedOption) {
        selectedOptions.push(selectedItem);
        // Remove unused values if needed
        displaySelectedOptions.push(_.omit(matchedOption, ['id', 'active']));
      }
    });

    this.setState({ selectedOptions, displaySelectedOptions });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  removeItem = (items: Object) => {
    let { selectedOptions, displaySelectedOptions } = this.state;

    const removedItems = _.difference(selectedOptions, items);

    removedItems.forEach(removedItem => {
      // Remove from selected options
      _.remove(selectedOptions, item => {
        return item === removedItem;
      });

      // Remove from display selected options
      _.remove(displaySelectedOptions, item => {
        return item.value === removedItem;
      });
    });

    this.setState({ selectedOptions, displaySelectedOptions });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  // Handle load more result
  onLoadMoreResults = () => {
    const { options: allOptions } = this.props;
    let { visibleOptionIndex, inputValue } = this.state;
    // Filter option list by input text
    if (inputValue) {
      this.nextPageWithFilter();
    } else {
      if (visibleOptionIndex < allOptions.length) {
        this.nextPage();
      }
    }
  };

  // Get next page
  nextPage = () => {
    const { options: allOptions } = this.props;
    let { visibleOptionIndex } = this.state;

    // Show loading icon
    this.setState({ loading: true });

    // Increase index
    const nextVisibleOptionIndex = visibleOptionIndex + this.paginationInterval;

    setTimeout(() => {
      let options = _.slice(allOptions, 0, nextVisibleOptionIndex);

      this.setState({
        visibleOptionIndex: nextVisibleOptionIndex,
        deselectedOptions: options,
        options,
        loading: false
      });
    }, 100);
  };

  // Get next page if having search text
  nextPageWithFilter = () => {
    const { options: allOptions } = this.props;
    let { visibleOptionIndex, latestSearchIndex: latest, inputValue, options } = this.state;

    // Stop pagination
    if (latest >= allOptions.length - 1) return;

    // Show loading icon
    this.setState({ loading: true });

    const { filterOptions = [], latestSearchIndex } = this.filter(
      inputValue,
      allOptions,
      latest + 1,
      this.paginationInterval
    );

    // Increase index
    let nextVisibleOptionIndex = visibleOptionIndex + this.paginationInterval;

    // Append to current list
    const newOptions = _.concat(options, filterOptions);

    this.setState({
      visibleOptionIndex: nextVisibleOptionIndex,
      latestSearchIndex,
      deselectedOptions: newOptions,
      options: newOptions,
      loading: false
    });
  };

  // Filter by text
  filter = (
    inputValue: string,
    allOptions: Array<{ value: string, label: string }>,
    start: number = 0,
    total: number
  ) => {
    let searchCount = 0;
    let latestSearchIndex = start;
    let filterOptions = [];

    try {
      // Exclude search character before replace special characters
      // ^: search string start with
      // $: search string end with
      const searchStartWithChar = _.startsWith(inputValue, '^');
      if (searchStartWithChar) inputValue = inputValue.substring(1);

      const searchEndWithChar = _.endsWith(inputValue, '$');
      if (searchEndWithChar) inputValue = inputValue.substring(0, inputValue.length - 1);

      // Add `\` before special character to allow search string containing special characters
      let searchData = !_.isEmpty(inputValue)
        ? inputValue.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')
        : '';

      // Re-insert search character
      if (searchStartWithChar) searchData = `^${searchData}`;
      if (searchEndWithChar) searchData = `${searchData}$`;

      const filterRegex = new RegExp(searchData, 'i');

      for (let i = start; i < allOptions.length; i++) {
        if (searchCount >= total) break;

        if (allOptions[i].label.match(filterRegex)) {
          searchCount++;
          filterOptions.push(allOptions[i]);
        }

        latestSearchIndex = i;
      }
    } catch (error) {}

    return { filterOptions, latestSearchIndex };
  };
}
